import { Link, useLocation, useNavigate } from "react-router";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { ArrowRightLeft, Eye, Trash2 } from "lucide-react";
import { KYC_STATUS_LABELS, createKarigarSchema, type Paginated, type UserDto } from "@gatiq/shared";
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Modal,
  Page,
  PageHeader,
  Pagination,
  RowActions,
  SearchInput,
  SelectField,
  SlideOver,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableSkeleton,
  TextArea,
  TextInput,
  Toolbar,
  ToolbarCount,
  statusTone,
  useToast,
} from "@/ui";
import { api, ApiClientError } from "@/lib/api";
import { ListFilters, useListFilters } from "@/components/ListFilters";
import { KarigarPlaceFields } from "@/components/KarigarPlaceFields";
import { useCan } from "@/auth/access";
import { clearFieldError, PhoneField, validateFieldOnBlur, validateForm } from "@/components/FormField";

const PAGE_SIZE = 20;

interface CreateForm {
  name: string;
  phone: string;
  counterId: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  district: string;
  village: string;
}

const EMPTY_FORM: CreateForm = { name: "", phone: "", counterId: "", address: "", city: "", state: "", pincode: "", district: "", village: "" };

export function KarigarsPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const can = useCan();
  const canCreate = can("karigars", "create");
  const canEdit = can("karigars", "edit");
  const canDelete = can("karigars", "delete");
  // The counter picker behind "Add" and "Transfer" needs the counters list.
  const pickCounters = Boolean((canCreate || canEdit) && can("counters"));
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const filters = useListFilters(() => setPage(1));
  const [data, setData] = useState<Paginated<UserDto> | null>(null);
  const [loading, setLoading] = useState(true);
  const [counters, setCounters] = useState<UserDto[]>([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<CreateForm>(EMPTY_FORM);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const [toDelete, setToDelete] = useState<UserDto | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [transferTarget, setTransferTarget] = useState<UserDto | null>(null);
  const [transferCounterId, setTransferCounterId] = useState("");
  const [transferReason, setTransferReason] = useState("");
  const [transferring, setTransferring] = useState(false);

  // The dropdown behind "Add karigar" and "Transfer". pageSize is capped at 100
  // server-side (shared paginationSchema); asking for more is a 400, which used
  // to be swallowed here and left both actions with no counter to pick.
  // ponytail: first 100 counters only; swap for a search-as-you-type picker once an org outgrows that.
  useEffect(() => {
    if (!pickCounters) return;
    api
      .get<Paginated<UserDto>>("/org/counters?page=1&pageSize=100")
      .then((res) => setCounters(res.items))
      .catch((err) => toast.error(err instanceof ApiClientError ? err.message : "Could not load counters."));
  }, [pickCounters, toast]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
      filters.apply(query);
      if (q.trim()) query.set("q", q.trim());
      const result = await api.get<Paginated<UserDto>>(`/org/karigars?${query.toString()}`);
      setData(result);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not load karigars.");
    } finally {
      setLoading(false);
    }
  }, [page, q, filters.apply, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  function karigarPayload(): unknown {
    return {
      name: form.name,
      phone: form.phone,
      counterId: form.counterId,
      address: form.address || undefined,
      city: form.city,
      state: form.state,
      pincode: form.pincode,
      district: form.district,
      village: form.village,
    };
  }

  async function handleCreate(event: FormEvent): Promise<void> {
    event.preventDefault();
    const validation = validateForm(createKarigarSchema, karigarPayload());
    if (!validation.success) {
      setFields(validation.fields);
      return;
    }
    setFields({});
    setSaving(true);
    try {
      const karigar = await api.post<UserDto>("/org/karigars", validation.data);
      toast.success(`${karigar.name} added.`);
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      await load();
    } catch (err) {
      if (err instanceof ApiClientError) {
        setFields(err.fields ?? {});
        toast.error(err.message);
      } else {
        toast.error("Could not add this karigar.");
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(): Promise<void> {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.del(`/org/karigars/${toDelete.id}`);
      toast.success(`${toDelete.name} removed.`);
      setToDelete(null);
      await load();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not remove this karigar.");
    } finally {
      setDeleting(false);
    }
  }

  function openTransfer(karigar: UserDto): void {
    setTransferTarget(karigar);
    setTransferCounterId("");
    setTransferReason("");
  }

  async function handleTransfer(): Promise<void> {
    if (!transferTarget || !transferCounterId) return;
    setTransferring(true);
    try {
      await api.post(`/org/karigars/${transferTarget.id}/transfer`, {
        counterId: transferCounterId,
        reason: transferReason || undefined,
      });
      toast.success(`${transferTarget.name} moved to a new counter.`);
      setTransferTarget(null);
      await load();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not transfer this karigar.");
    } finally {
      setTransferring(false);
    }
  }

  const counterOptions = counters.map((c) => ({ value: c.id, label: c.name }));

  return (
    <Page bleed>
      <PageHeader
        title="Karigars"
      />

      <Card fill>
        <Toolbar
          trailing={
            <>
              {data ? <ToolbarCount count={data.total} noun="karigar" /> : null}
              {canCreate ? <Button onClick={() => setCreateOpen(true)}>+ Add karigar</Button> : null}
            </>
          }
        >
          <SearchInput
            label="Search karigars"
            onChange={(value) => {
              setQ(value);
              setPage(1);
            }}
            placeholder="Search by name, phone…"
            value={q}
          />
          <ListFilters definitions={data?.filters} filters={filters} />
        </Toolbar>

        {loading ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <TableSkeleton cols={5} />
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <EmptyState
              action={canCreate ? <Button onClick={() => setCreateOpen(true)}>+ Add karigar</Button> : undefined}
              message="Karigars appear here once registered by hand or via a counter's QR."
              title="No karigars yet"
            />
          </div>
        ) : (
          <Table fill searchable={false} filterable={false}>
            <THead>
              <TR>
                <TH>Name</TH>
                <TH>Phone</TH>
                <TH>Counter</TH>
                <TH>KYC</TH>
                <TH align="right" width="4.5rem">
                  <span className="sr-only">Actions</span>
                </TH>
              </TR>
            </THead>
            <tbody>
              {data.items.map((k) => (
                <TR key={k.id} onClick={() => navigate(`${location.pathname}/${k.id}`)}>
                  <TD><Link className="font-medium text-brand-ink hover:underline" to={`${location.pathname}/${k.id}`} onClick={(event) => event.stopPropagation()}>{k.name}</Link></TD>
                  <TD>{k.phone}</TD>
                  <TD>{k.counterName ?? "—"}</TD>
                  <TD>
                    <Badge tone={statusTone(k.kycStatus ?? "")}>
                      {k.kycStatus ? KYC_STATUS_LABELS[k.kycStatus] : "—"}
                    </Badge>
                  </TD>
                  <TD align="right">
                    <RowActions
                      items={[
                        { label: "View profile", icon: Eye, onSelect: () => navigate(`${location.pathname}/${k.id}`) },
                        {
                          label: "Transfer to counter",
                          icon: ArrowRightLeft,
                          hidden: !canEdit,
                          onSelect: () => openTransfer(k),
                        },
                        { label: "Delete", icon: Trash2, danger: true, hidden: !canDelete, onSelect: () => setToDelete(k) },
                      ]}
                      label={k.name}
                    />
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        )}
        {data ? (
          <div className="shrink-0">
            <Pagination onChange={setPage} page={data.page} total={data.total} totalPages={data.totalPages} />
          </div>
        ) : null}
      </Card>

      <SlideOver
        footer={
          <>
            <Button onClick={() => setCreateOpen(false)} variant="secondary">
              Cancel
            </Button>
            <Button form="create-karigar" loading={saving} type="submit">
              Create
            </Button>
          </>
        }
        onClose={() => setCreateOpen(false)}
        open={createOpen}
        title="Add karigar"
      >
        <form className="flex flex-col gap-4" id="create-karigar" noValidate onSubmit={handleCreate}>
          <TextInput
            autoComplete="name"
            error={fields.name}
            label="Full name"
            onBlur={() => validateFieldOnBlur(createKarigarSchema, karigarPayload(), "name", setFields)}
            onChange={(e) => {
              setForm((f) => ({ ...f, name: e.target.value }));
              clearFieldError(setFields, "name");
            }}
            required
            value={form.name}
          />
          <PhoneField
            error={fields.phone}
            hint="10-digit mobile number"
            label="Mobile"
            onBlur={() => validateFieldOnBlur(createKarigarSchema, karigarPayload(), "phone", setFields)}
            onChange={(digits) => {
              setForm((f) => ({ ...f, phone: digits }));
              clearFieldError(setFields, "phone");
            }}
            required
            value={form.phone}
          />
          <SelectField
            error={fields.counterId}
            label="Counter"
            onBlur={() => validateFieldOnBlur(createKarigarSchema, karigarPayload(), "counterId", setFields)}
            onChange={(e) => {
              setForm((f) => ({ ...f, counterId: e.target.value }));
              clearFieldError(setFields, "counterId");
            }}
            options={counterOptions}
            placeholder="Select a counter"
            required
            value={form.counterId}
          />
          <KarigarPlaceFields value={form} errors={fields} onChange={(patch) => {
            setForm((previous) => ({ ...previous, ...patch }));
            for (const key of Object.keys(patch)) clearFieldError(setFields, key);
          }} />
        </form>
      </SlideOver>

      <ConfirmDialog
        loading={deleting}
        message={`Remove ${toDelete?.name ?? "this karigar"}? This cannot be undone.`}
        onClose={() => setToDelete(null)}
        onConfirm={() => void handleDelete()}
        open={Boolean(toDelete)}
        title="Remove karigar"
      />

      <Modal
        footer={
          <>
            <Button onClick={() => setTransferTarget(null)} variant="secondary">
              Cancel
            </Button>
            <Button disabled={!transferCounterId} loading={transferring} onClick={() => void handleTransfer()}>
              Transfer
            </Button>
          </>
        }
        onClose={() => setTransferTarget(null)}
        open={Boolean(transferTarget)}
        title={`Transfer ${transferTarget?.name ?? ""}`}
        width="sm"
      >
        <div className="flex flex-col gap-4">
          <SelectField
            label="New counter"
            onChange={(e) => setTransferCounterId(e.target.value)}
            options={counterOptions.filter((c) => c.value !== transferTarget?.counterId)}
            placeholder="Select a counter"
            value={transferCounterId}
          />
          <TextArea
            label="Reason (optional)"
            onChange={(e) => setTransferReason(e.target.value)}
            value={transferReason}
          />
        </div>
      </Modal>
    </Page>
  );
}
